<template>
  <div v-if="payment">
    <h1>Payment</h1>
    <v-btn :to="computedDiaryLink">
      <v-icon>chevron_left</v-icon>Payments diary
    </v-btn>
    <v-btn @click="deletePayment()" color="error">
      <v-icon>delete</v-icon>
    </v-btn>
    <MatterSearch
      :disabled="!$route.params.id"
      :fileid="payment.fileid"
      :freereference="payment.freereference"
      @updateFile="updateFile"
      @updateFreeReference="updateFreeReference"
      @unlink="unlink"
    />
    <v-form ref="formPayment" v-model="valid" lazy-validation>
      <v-layout row wrap>
        <v-flex xs12 md6 v-if="$route.params.id">
          <v-text-field
            label="Payment id"
            v-model="payment.id"
            :readonly="true"
            outline
          />
        </v-flex>
        <v-flex xs12 md6 v-if="$route.params.id">
          <DateDropdown label="Date" v-model="payment.timestamp" />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="bankname"
            label="Bank name"
            v-model="payment.bankname"
            :rules="lengthRules.concat(valueRules)"
            :counter="30"
            browser-autocomplete="on"
            outline
          />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="sortcode"
            type="number"
            label="Sort code"
            v-model="payment.sortcode"
            :rules="lengthRules.concat(valueRules)"
            :counter="30"
            browser-autocomplete="on"
            outline
          />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="accountnumber"
            type="number"
            label="Bank ac no"
            v-model="payment.bankacno"
            :rules="lengthRules.concat(valueRules)"
            :counter="30"
            browser-autocomplete="on"
            outline
          />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="beneficiary"
            label="Beneficiary"
            v-model="payment.beneficiary"
            :rules="valueRules"
            browser-autocomplete="on"
            outline
          />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="amount"
            type="number"
            label="Amount"
            v-model.number="payment.amount"
            :rules="valueRules"
            outline
          />
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="time"
            label="Time"
            v-model="payment.timesent"
            v-mask="'##:##'"
            outline
          >
            <template slot="append" v-if="payment.timesent">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <a @click="payment.timesent = null" v-on="on">
                    <v-icon>clear</v-icon>
                  </a>
                </template>
                <span>Clear the time</span>
              </v-tooltip>
            </template>
            <template slot="append-outer">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <a @click="updateTimeSent()" v-on="on">
                    <v-icon>access_time</v-icon>
                  </a>
                </template>
                <span>Set time to now</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-flex>
      </v-layout>
      <v-btn @click="savePayment()"> <v-icon>save</v-icon>Save </v-btn>
    </v-form>
  </div>
</template>

<script>
import MatterSearch from "@/components/file/matter/MatterSearch.vue";
import DateDropdown from "@/components/date/DateDropdown";
import nbmlaw from "@/api/nbmlaw";
import { format, parseISO } from "date-fns";
export default {
  name: "Payment",
  metaInfo() {
    return {
      title: "Payment",
      titleTemplate: "%s - Fundview - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { DateDropdown, MatterSearch },
  computed: {
    computedDiaryLink() {
      return { name: "paymentsDiary", query: { date: this.date } };
    }
  },
  data() {
    return {
      valid: false,
      payment: null,
      date: null,
      lengthRules: [
        (v) =>
          !v || (v && v.length <= 30) || "This must be less than 30 characters"
      ],
      valueRules: [(v) => !!v || "Value is required"]
    };
  },
  methods: {
    async fetchPayment() {
      const response = await nbmlaw.get(
        `/cors/admin/fundview/payments/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.payment = response.data;
    },
    updateTimeSent() {
      this.payment.timesent = format(new Date(), "HH:mm");
    },
    async updateFile(id) {
      await nbmlaw.put(
        `/cors/admin/fundview/payments/${this.$route.params.id}/file/${id}`,
        null
      );
      // console.log(response.data);
      this.fetchPayment();
    },
    async updateFreeReference(value) {
      await nbmlaw.put(
        `/cors/admin/fundview/payments/${this.$route.params.id}/freeReference`,
        value
      );
      // console.log(response.data);
      this.fetchPayment();
    },
    async unlink() {
      await nbmlaw.put(
        `/cors/admin/fundview/payments/${this.$route.params.id}/unlink`,
        null
      );
      // console.log(response.data);
      this.fetchPayment();
    },
    savePayment() {
      if (this.$refs.formPayment.validate()) {
        if (this.$route.params.id) {
          this.updatePayment();
        } else {
          this.createPayment();
        }
      }
    },
    async updatePayment() {
      await nbmlaw.put(
        `/cors/admin/fundview/payments/${this.$route.params.id}`,
        this.payment
      );
      // console.log(response.data);
      this.$router.push(this.computedDiaryLink);
    },
    async createPayment() {
      const response = await nbmlaw.post(
        `/cors/admin/fundview/payments`,
        this.payment
      );
      // console.log(response.data);
      this.$router.push({ name: "payment", params: { id: response.data } });
    },
    async deletePayment() {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/fundview/payments/${this.$route.params.id}`
        );
        // console.log(response.data);
        this.$router.push(this.computedDiaryLink);
      }
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        if (this.$route.params.id) {
          this.fetchPayment();
        } else {
          this.payment = { timestamp: new Date().toISOString() };
        }
      }
    },
    payment: {
      handler() {
        this.date = this.payment.timestamp
          ? format(parseISO(this.payment.timestamp), "yyyyMMdd")
          : null;
      }
    }
  }
};
</script>
